import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Paper,
  Box,
  Avatar,
  Typography,
  Grid,
  Link,
  Breadcrumbs,
  CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as EditIcon } from '../../../../assets/images/edit.svg';
import { ReactComponent as ArchiveIcon } from '../../../../assets/images/box-red.svg';
import { ReactComponent as UnarchiveIcon } from '../../../../assets/images/green-box.svg';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomModal } from '../../../../components/CustomModal';
import { ROUTES, getRouteWithParam, ROUTE_FOR_ASSETS } from '../../../../constants/routes';
import { StatusColor } from '../../../../components/StatusColor';
import {
  actionsArchivePartner,
  archivePartner,
  archivePromotion,
  getPartnerById,
  getPromotions, unarchivePartner
} from '../../../../store/partners/actions';
import { Tab } from '../../../../components/Tab';
import { formatDate } from '../../../../utils/formatDate';
import { ReactComponent as ClockIcon } from '../../../../assets/images/clock.svg';
import { CustomExpand } from '../../../../components/CustomExpand';
import EnhancedTable from '../../../../components/EnhancedTable';
import { PROMOTIONS_HEAD_CELLS } from '../../constants';
import { PromotionsTableBody } from '../../components/PromotionsTableBody';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { ReactComponent as ReportRedIcon } from '../../../../assets/images/report_red.svg';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const TABS = [
  'DETAILS',
  'EVENTS'
]

export const DetailPartner = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { partnerCard, promotions, fetchingPromotions, errorArchive } = useSelector((state) => state.partners);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const [tabName, setTabName] = useState(TABS[0]);

  const [isModalOpenedArchive, setIsModalOpenedArchive] = useState(false);
  const [isModalOpenedUnarchive, setIsModalOpenedUnarchive] = useState(false);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [filter, setFilter] = useState({
    order_by: 'cms_package_name',
    order_type: 'asc',
    page: 0,
    size: 10,
  })
  const [curID, setCurID] = useState('');

  const isArchived = partnerCard.status === 'Архив';

  useEffect(() => {
    dispatch(getPartnerById(Number(id)));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (errorArchive) setErrorModal(true);
  }, [errorArchive]);

  useEffect(() => {
    isAllowedToListPromotions && dispatch(getPromotions({
      ...filter,
      page: filter.page + 1,
      partner_id: id
    }))
    // eslint-disable-next-line
  }, [
    filter.page,
    filter.size,
    filter.order_by,
    filter.order_type
  ]);

  const isAllowedToArchive = visibleBySectionPermission(permissions, PERMISSIONS.PARTNERS.archivePartners.value);
  const isAllowedToEdit = visibleBySectionPermission(permissions, PERMISSIONS.PARTNERS.updatePartners.value);
  const isAllowedToListPromotions = visibleBySectionPermission(permissions, PERMISSIONS.PARTNER.listPartnerPromotions.value);
  const isAllowedToRetrieve = visibleBySectionPermission(permissions, [PERMISSIONS.PARTNERS.retrievePartners.value]);
  const isAllowedToHistory = visibleBySectionPermission(permissions, PERMISSIONS.LOGGING.ListHistory.value);

  if (!isAllowedToRetrieve) return null;

  return (
    <>
      {(partnerCard?.id !== +id || !Object.values(partnerCard).length || fetchingPromotions) ? (
        <Box
          sx={{
            marginTop: '150px',
            textAlign: 'center'
          }}>
          <CircularProgress data-testid="CircularProgress" />
        </Box>
      ) : (
        <>
          <Breadcrumbs mb="16px" aria-label="breadcrumb">
            <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.PARTNERS}>
              Партнёры
            </Link>
            <Typography color="text.secondary">
              {partnerCard?.name}
            </Typography>
          </Breadcrumbs>
          <Paper
            sx={{ padding: '32px' }}
          >
            <Box
              sx={{
                display: 'flex'
              }}>
              <Avatar
                alt={partnerCard?.name}
                src={`${ROUTE_FOR_ASSETS}/partners/${partnerCard?.logo}`}
                sx={{ width: 128, height: 128, marginRight: '32px' }}
              />

              <Box
                sx={{
                  width: '100%',
                  display: 'flex'
                }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '510px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <Typography color="text.secondary" sx={{ fontSize: '18px' }}>
                    {partnerCard?.id}
                  </Typography>
                  <Typography variant="h1" mb="24px">
                    {partnerCard?.name}
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    {partnerCard?.status === 'Действующий' &&
                      <Box>
                        <CustomButton
                          color="secondary"
                          variant="outlined"
                          size="small"
                          sx={{ marginRight: '16px', height: '42px' }}
                          startIcon={<EditIcon />}
                          component={RouterLink}
                          to={getRouteWithParam(ROUTES.PARTNERS_ADD_PARTNER, ':id', id)}
                          disabled={!isAllowedToEdit}
                          tooltip={isAllowedToEdit ? null : DISABLED_CONTROL_TOOLTIP}
                        >
                          Редактировать
                        </CustomButton>
                      </Box>
                    }
                    <Box>
                      <CustomButton
                        color="secondary"
                        variant="outlined"
                        size="small"
                        startIcon={<ClockIcon />}
                        sx={{ marginRight: '16px', height: '42px', width: '200px' }}
                        component={RouterLink}
                        to={getRouteWithParam(ROUTES.PARTNERS_HISTORY_PARTNER, ':id', id)}
                        disabled={!isAllowedToHistory}
                        tooltip={isAllowedToHistory ? null : DISABLED_CONTROL_TOOLTIP}
                      >
                        История событий
                      </CustomButton>
                    </Box>
                    {partnerCard?.status === 'Действующий' &&
                      <Box>
                        <CustomButton
                          color="error"
                          variant="outlined"
                          size="small"
                          startIcon={<ArchiveIcon />}
                          onClick={isAllowedToArchive ? () => setIsModalOpenedArchive(true) : undefined}
                          sx={{ width: '140px' }}
                          disabled={!isAllowedToArchive}
                          tooltip={isAllowedToArchive ? null : DISABLED_CONTROL_TOOLTIP}
                        >
                          В архив
                        </CustomButton>
                      </Box>
                    }
                    {isArchived &&
                      <Box>
                        <CustomButton
                          color="success"
                          variant="outlined"
                          size="small"
                          startIcon={<UnarchiveIcon />}
                          onClick={isAllowedToArchive ? () => setIsModalOpenedUnarchive(true) : undefined}
                          disabled={!isAllowedToArchive}
                          tooltip={isAllowedToArchive ? null : DISABLED_CONTROL_TOOLTIP}
                        >
                          Вернуть в работу
                        </CustomButton>
                      </Box>
                    }
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'end'
                  }}>
                  { partnerCard?.status && <StatusColor status={partnerCard?.status} variant="subtitle2" /> }
                </Box>
              </Box>
            </Box>
          </Paper>

          <Box display="flex" justifyContent="space-between">
            <Box display="flex">
              <Tab
                label="Детализация"
                setTypeTable={() => setTabName(TABS[0])}
                typeCurrent={tabName}
                typeDefault={TABS[0]}
              />
              {isAllowedToListPromotions ? <Tab
                label='Партнёрские акции'
                setTypeTable={() => setTabName(TABS[1])}
                typeCurrent={tabName}
                typeDefault={TABS[1]}
              /> : null}
            </Box>
          </Box>

          {tabName === TABS[0] && (
            <Paper
              sx={{
                padding: '24px'
              }}>
              <Grid container>
                <Grid item xs={isArchived ? 1.5 : 1.75}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      БИН
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500
                      }}>
                      {partnerCard?.bin_number}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={isArchived ? 2 : 2.25}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      Номер телефона
                    </Typography>
                    {partnerCard?.phones?.length ?
                      <CustomExpand
                        items={partnerCard?.phones}
                        node={(item) =>
                          <Typography
                            sx={{ fontWeight: 500, fontSize: '20px' }}
                            key={`item${item}`}
                          >
                            +{item}
                          </Typography>
                        }
                      />
                      : '-'
                    }
                  </Box>
                </Grid>
                <Grid item xs={isArchived ? 2 : 2.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      Email
                    </Typography>
                    <Link
                      href="mailto:myemail@gmail.com"
                      variant="subtitle2"
                      color="primary.main"
                      underline="hover"
                      sx={{
                        fontWeight: 500
                      }}>
                      {partnerCard.email}
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={isArchived ? 1 : 1.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      Города
                    </Typography>
                    { partnerCard.cities?.length ? <CustomExpand items={partnerCard.cities} /> : '-' }
                  </Box>
                </Grid>
                <Grid item xs={isArchived ? 1.75 : 2}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      Контактное лицо
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500
                      }}>
                      {partnerCard?.contact_person || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={1.75}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <Typography variant="body1" color="text.secondary">
                      Дата создания
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500
                      }}>
                      {formatDate(partnerCard.created_at)}
                    </Typography>
                  </Box>
                </Grid>
                {isArchived &&
                  <Grid item xs={1.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                      <Typography variant="body1" color="text.secondary">
                        Дата архивации
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 500
                        }}>
                        {formatDate(partnerCard.updated_at)}
                      </Typography>
                    </Box>
                  </Grid>
                }
              </Grid>
            </Paper>
          )}

          {tabName === TABS[1] &&
          <Box data-testid="PromotionsTab">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="24px">
              {/* !isArchived && (
                <CustomButton
                  component={Link}
                  to={{ pathname: ROUTES.PARTNERS_ADD_PROMOTION, state: { from: location?.pathname } }}
                  size="small"
                  mr="24px"
                  variant="contained"
                >
                  Создать акцию
                </CustomButton>
              ) */}
            </Box>

            {
              promotions?.items?.length ?
                <EnhancedTable
                  rows={promotions?.items}
                  headCells={PROMOTIONS_HEAD_CELLS}
                  filter={filter}
                  total={promotions?.total || 0}
                  fetching={fetchingPromotions}
                  heightRow={77}
                  setFilter={setFilter}
                >
                  <PromotionsTableBody
                    data={promotions?.items}
                    openModal={() => setIsSuccessModal(true)}
                    setCurID={setCurID}
                    permissions={{
                      edit: visibleBySectionPermission(permissions, PERMISSIONS.PARTNER.updatePartnerPromotions.value),
                      retrieve: visibleBySectionPermission(permissions, PERMISSIONS.PARTNER.retrievePartnerPromotions.value),
                      copy: visibleBySectionPermission(permissions, PERMISSIONS.PARTNER.copyPartnerPromotions.value),
                      archive: visibleBySectionPermission(permissions, PERMISSIONS.PARTNER.archivePartnerPromotions.value)
                    }}
                  />
                </EnhancedTable>
                : <NotFoundItems message='У данного партнёра ещё нет акций' notFoundIcon />
            }

            <CustomModal
              opened={isSuccessModal}
              modalClose={() => setIsSuccessModal(false)}
              modalSubmit={() => {
                dispatch(archivePromotion(curID));
                dispatch(getPromotions({
                  ...filter,
                  page: filter.page + 1,
                  partner_id: id
                }));
                setIsSuccessModal(false);
              }}
              text="Вы действительно хотите перевести данную партнёрскую акцию в Архив?"
              cancelBtnName="Отмена"
              submitBtnName="Да"
              data-testid="Warning Modal"
            />
          </Box>
          }

          <CustomModal
            opened={isModalOpenedArchive}
            modalClose={() => setIsModalOpenedArchive(false)}
            modalSubmit={() => {
              dispatch(archivePartner(id));
              setIsModalOpenedArchive(false);
            }}
            text={`Вы действительно хотите добавить компанию ${partnerCard?.name} в Архив?`}
            cancelBtnName="Отмена"
            submitBtnName="Да"
          />

          <CustomModal
            opened={isModalOpenedUnarchive}
            modalClose={() => setIsModalOpenedUnarchive(false)}
            modalSubmit={() => {
              dispatch(unarchivePartner(id));
              setIsModalOpenedUnarchive(false);
            }}
            text={`Вы действительно хотите вернуть компанию ${partnerCard?.name} в работу?`}
            cancelBtnName="Отмена"
            submitBtnName="Да"
          />

          <CustomModal
            opened={errorModal}
            modalClose={() => {
              setErrorModal(false);
              dispatch(actionsArchivePartner.fail(null));
            }}
            text={errorArchive}
            cancelBtnName="Отмена"
            withoutSubmit
            icon={<ReportRedIcon />}
          />
        </>
      )}
    </>
  );
};
